import { LegalPageShell, LegalSection } from '../components/LegalPageShell'
import { usePageMeta } from '../hooks/usePageMeta'

const UPDATED = '20 September 2026'

export function PrivacyPage() {
  usePageMeta(
    'Privacy Policy | YellowDots',
    'Learn how YellowDots collects, uses and protects your information on our local learning discovery platform.',
  )
  return (
    <LegalPageShell title="Privacy Policy" updated={UPDATED}>
      <p>
        This Privacy Policy explains what YellowDots AI, operated by mySATHI Foundation ("Yellow
        Dots", "we", "us", the <span className="font-semibold text-deep-900">data fiduciary</span>{' '}
        under India's Digital Personal Data Protection Act, 2023 ("DPDP Act")), collects when you
        use yellowdots.ai and our applications (the "Platform"), why we collect it, who we share it
        with, and the rights you have over it. It should be read alongside our{' '}
        <a href="/terms" className="font-semibold text-dot-700 underline underline-offset-2">Terms &amp; Conditions</a>.
      </p>
      <p>
        This Policy applies whether you registered yourself, were added by a parent or guardian on
        your behalf, or were onboarded in bulk by an administrator from a list an institution
        provided - for example, a coaching centre uploaded by our operations team from an
        enrollment sheet.
      </p>

      <LegalSection title="1. Who can use the Platform">
        <p>
          A learner may register at any age. If they are under 18, a parent or guardian must be
          linked to the account and approve every connection request before it takes effect - see
          Section 8. Everyone else (a tutor, coaching centre, counsellor, school, college or
          company) must be able to enter into a binding agreement to register.
        </p>
      </LegalSection>

      <LegalSection title="2. What we collect">
        <p>What we collect depends on which side of the Platform you use.</p>
        <p className="font-semibold text-deep-900">If you are a learner (or a guardian registering on a child's behalf)</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li><span className="font-semibold text-deep-900">Account details</span> - full name, email address, mobile number, password (stored as a salted hash, never in plain text), date of birth and gender. Date of birth decides whether the account is a minor's, which is what puts every connection behind a parent's approval.</li>
          <li><span className="font-semibold text-deep-900">Location</span> - city, state, pincode and, where you provide it, a full address and house number (encrypted), used to place your dot on the map and match you with nearby providers.</li>
          <li><span className="font-semibold text-deep-900">What you're looking for</span> - a category and, optionally, a more specific subject or need, used to match you with relevant providers.</li>
          <li><span className="font-semibold text-deep-900">Guardian details</span> - for a learner under 18, a parent or guardian's mobile number, their relationship to the learner, and their approval decisions on connection requests.</li>
        </ul>
        <p className="font-semibold text-deep-900">If you are a provider (a tutor, centre, counsellor, school, college or company)</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li><span className="font-semibold text-deep-900">Account details</span> - full name, email address, mobile number, password, and gender.</li>
          <li><span className="font-semibold text-deep-900">Location</span> - city, state, pincode and, where relevant, an address and coverage radius.</li>
          <li><span className="font-semibold text-deep-900">What you offer</span> - the category and specific subjects, skills or services you provide, your bio, qualifications, years of experience and languages spoken.</li>
          <li><span className="font-semibold text-deep-900">Verification details</span> - an ID type and, where you submit one for verification, an ID number (stored as a one-way hash, not in plain text), used to grant a verification tier shown on your public listing.</li>
        </ul>
        <p className="font-semibold text-deep-900">Everyone</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li><span className="font-semibold text-deep-900">Usage data</span> - the connections you send, receive and approve, and basic technical logs (IP address, browser) kept for security and abuse prevention.</li>
          <li><span className="font-semibold text-deep-900">Consent records</span> - when you accepted our Terms and this Policy, and the timestamp of any connection-related consent you give.</li>
        </ul>
        <p>
          Where an administrator uploads an account on your behalf - for example, from a spreadsheet
          of tutors an institution asked us to onboard - the same fields above are collected from
          that file, and that upload is itself the institution's representation to us that consent
          was obtained from the people listed in it.
        </p>
      </LegalSection>

      <LegalSection title="3. How your information is shown to others">
        <p>
          The Platform protects your data with a two-tier visibility model.
        </p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li><span className="font-semibold text-deep-900">Before a connection:</span> only non-identifying information is visible - a provider's public profile (name, bio, qualifications, category, verification tier) and an approximate location on the map. Your exact phone number, email address and precise address are never shown here.</li>
          <li><span className="font-semibold text-deep-900">After a connection is accepted:</span> once both sides have confirmed a connection, your phone number and email address are shared with the other party so you can make direct contact. This never happens without your acceptance.</li>
          <li><span className="font-semibold text-deep-900">Guardians:</span> a guardian linked to a minor's account can see that child's activity and pending connection requests, as part of the approval flow they agreed to when linking.</li>
        </ul>
      </LegalSection>

      <LegalSection title="4. How we use it">
        <ul className="list-disc space-y-1.5 pl-5">
          <li>To create and secure your account, and to sign you in;</li>
          <li>To place your dot on the map and match you with relevant nearby learners or providers;</li>
          <li>To show a provider's public profile to learners searching nearby, and a learner's stated need to matching providers;</li>
          <li>To route a minor's connection requests to their linked guardian for approval before they take effect;</li>
          <li>To send account emails - a welcome note, password resets, guardian invites and connection notifications;</li>
          <li>To investigate abuse reports and enforce our Terms; and</li>
          <li>To maintain audit logs of admin actions - such as verifying, suspending or correcting an account - for accountability.</li>
        </ul>
        <p>
          We will not use your personal data for a purpose beyond those described in this Policy
          without informing you and, where required, seeking your consent. We do not sell your
          personal data, and we do not use it for third-party advertising.
        </p>
      </LegalSection>

      <LegalSection title="5. Who we share your data with">
        <p>
          We share personal data with third parties only where necessary to run the Platform, and
          only for the specific purpose stated below. We do not sell it, and we do not share it for
          any third party's own marketing.
        </p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li><span className="font-semibold text-deep-900">Cloud hosting</span> - our database and application servers are hosted on AWS, in India (Mumbai region), to run and store the Platform.</li>
          <li><span className="font-semibold text-deep-900">Geocoding</span> - when you provide a pincode, it is sent to OpenStreetMap's Nominatim service to resolve it to an approximate map coordinate. Your exact address is never sent for this purpose.</li>
          <li><span className="font-semibold text-deep-900">Email delivery</span> - your email address is used by our email delivery provider solely to send you account, password-reset and connection notifications.</li>
        </ul>
        <p>
          We do not currently use SMS, WhatsApp, voice calling or AI/voice-bot services, and no
          personal data is sent to an advertising or analytics network. We may also share personal
          data with government authorities, regulators or courts where required by law, or to
          protect the rights, property or safety of our users.
        </p>
      </LegalSection>

      <LegalSection title="6. How long we keep it">
        <ul className="list-disc space-y-1.5 pl-5">
          <li><span className="font-semibold text-deep-900">Account and profile data</span> - kept while your account is active, and deleted or anonymised within a reasonable period after you delete your account, except where we must retain records by law.</li>
          <li><span className="font-semibold text-deep-900">Consent and audit records</span> - kept for the duration your account is active and for a further period afterwards, to meet our accountability obligations under the DPDP Act.</li>
          <li><span className="font-semibold text-deep-900">Security and backup logs</span> - kept for a limited period to support security monitoring and disaster recovery.</li>
        </ul>
        <p>
          Personal data you have already shared with another user through an accepted connection
          remains with that other party - we cannot retrieve or delete it on your behalf once it has
          been shared.
        </p>
      </LegalSection>

      <LegalSection title="7. How we keep it secure">
        <p>
          Sensitive personal fields - phone number, email address, date of birth and pincode - are
          encrypted at rest. Where the Platform needs to check whether an email or phone number is
          already registered, it does so against a one-way cryptographic hash rather than the plain
          value, so a database copy alone cannot be searched by phone number or email. Access to
          personal data is restricted to authorised personnel, and our database is hosted in India.
          No system is completely secure; in the event of a personal data breach likely to affect
          your rights, we will notify you and the relevant authorities as required by law.
        </p>
      </LegalSection>

      <LegalSection title="8. Children's data">
        <p>
          A learner may register while under 18, but every connection they send or receive is held
          for a linked parent or guardian's approval before it takes effect, and that guardian can
          see the child's activity on the Platform. We collect the minimum needed to run this
          safeguard - a guardian's mobile number, relationship, and their approval decisions - and
          do not use a minor's data for anything beyond operating the Platform. If we become aware
          that a minor's data was collected without a guardian's consent, we will delete it as soon
          as possible; contact us using Section 11 if you believe this has happened.
        </p>
      </LegalSection>

      <LegalSection title="9. Cookies and local storage">
        <p>
          The Platform stores a session token on your device so you stay signed in between visits.
          We do not use third-party advertising or tracking cookies.
        </p>
      </LegalSection>

      <LegalSection title="10. Your rights">
        <p>Under the DPDP Act, you have the following rights over your personal data:</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li><span className="font-semibold text-deep-900">Access and correction</span> - review and update most of your profile details directly from your account.</li>
          <li><span className="font-semibold text-deep-900">Erasure</span> - request deletion of your account and data.</li>
          <li><span className="font-semibold text-deep-900">Withdraw consent</span> - withdraw consent at any time; this does not affect anything that happened before you withdrew.</li>
          <li><span className="font-semibold text-deep-900">Grievance redressal</span> - raise a concern as described in Section 11.</li>
          <li><span className="font-semibold text-deep-900">Nominate</span> - nominate another individual to exercise these rights on your behalf in the event of your death or incapacity, by writing to us at the email below.</li>
        </ul>
        <p>
          To exercise any of these rights, write to{' '}
          <a href="mailto:support@yellowdots.ai" className="font-semibold text-dot-700 underline underline-offset-2">support@yellowdots.ai</a>{' '}
          from your registered email address. We will act on a request except where we are required
          to retain records by law.
        </p>
      </LegalSection>

      <LegalSection title="11. Grievances and the Data Protection Board">
        <p>
          If you have a concern about how your personal data has been handled, including a possible
          data breach, contact us at{' '}
          <a href="mailto:support@yellowdots.ai" className="font-semibold text-dot-700 underline underline-offset-2">support@yellowdots.ai</a>.
          We will acknowledge your complaint promptly and aim to resolve it within 60 days.
        </p>
        <p>
          If you are not satisfied with our response, you may escalate to the Data Protection Board
          of India, established under the DPDP Act. Visit{' '}
          <a href="https://www.meity.gov.in/dpdp" target="_blank" rel="noreferrer" className="font-semibold text-dot-700 underline underline-offset-2">meity.gov.in/dpdp</a>{' '}
          for updates on the Board's complaint process.
        </p>
      </LegalSection>

      <LegalSection title="12. Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes will be reflected by
          updating the date at the top of this page.
        </p>
      </LegalSection>

      <LegalSection title="13. Contact us">
        <p>
          Questions about this Policy or how we handle your personal data can be sent to{' '}
          <a href="mailto:support@yellowdots.ai" className="font-semibold text-dot-700 underline underline-offset-2">support@yellowdots.ai</a>.
        </p>
      </LegalSection>
    </LegalPageShell>
  )
}
